import { useMutation } from '@apollo/react-hooks';
import { MenuIcon } from '@heroicons/react/solid';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import useOnClickOutside from '../../hooks/useClickOutside';
import {
  DELETE_DISCORD_CONNECTION,
  SET_PRIMARY_DISCORD_CONNECTION,
  UPDATE_USER,
} from '../../lib/graphql/mutations';
import { COLORS } from '../../theme';
import { TidalButton } from '../common/button';
import TidalInputField from '../common/input/TidalInputField';
import ConnectionButton from '../externalPlatforms/ConnectionButton';
import { Box, Column, FlexGrow, Row } from '../Flex';
import { BackgroundButton, EditableInput } from '../Form';
import DiscordIcon from '../svgs/DiscordIcon';
import KebabMenu from '../svgs/KebabMenu';
import { analytics } from '../../lib/analytics';

const Container = styled.div`
  width: 100%;
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 5px 36px #919db529;
  border: 2px solid #e1eaf3;
  border-radius: 15px;
  opacity: 1;
  @media only screen and (max-width: 1200px) {
    max-width: 100%;
  }
  @media only screen and (max-width: 1100px) {
    border: 1px solid #e5e8f0;
    box-shadow: 0 0;
  }
`;

const Contents = styled.div`
  padding: 1.5rem 2rem;
  @media only screen and (max-width: 500px) {
    padding: 16px;
  }
`;

const Title = styled.p`
  text-align: left;
  font: normal normal 600 16px/25px Poppins;
  letter-spacing: 0px;
  color: ${COLORS.BLACK};
  opacity: 1;
`;

const InputTitle = styled.p`
  text-align: left;
  font: normal normal 500 10px/16px Poppins;
  letter-spacing: 0px;
  color: #717884;
  opacity: 1;
  margin-top: 1.5rem;
  margin-bottom: 0.55rem;
`;

const ProfileButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 210px;
  align-items: center;
  @media only screen and (max-width: 500px) {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;
    grid-gap: 8px;
  }
`;

const ProfileDiscordConnection = styled.div<{
  isDefault: boolean;
}>`
  height: 50px;
  display: grid;
  grid-template-columns: ${(props) =>
    props.isDefault ? '24px 1fr 80px' : '24px 1fr 24px'};
  grid-gap: 8px;
  align-items: center;
  background-color: #f2f4f9;
  padding: 0 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  font-size: 14px;
  position: relative;
  .discord-kebab-menu {
    display: flex;
    justify-content: flex-end;
  }
  @media only screen and (max-width: 450px) {
    .discord-id {
      overflow: hidden;
    }
  }
  & > .default-discord {
    color: #1cbaf0;
    text-align: right;
  }

  @media only screen and (max-width: 545px) {
    grid-template-columns: ${(props) =>
      props.isDefault ? '0.2fr 1fr 0.3fr' : '0.2fr 1fr 5fr'};
  }
`;

const ProfileDiscordConnectionMenu = styled.div<{ isVisible: boolean }>`
  border: ${(props) =>
    props.isVisible
      ? `1px solid ${props.theme.colors.borderColors[0]}`
      : 'none'};
  border-radius: 10px;
  box-shadow: 0px 10px 49px rgb(0 27 31 / 12%);
  position: absolute;
  top: 50px;
  right: 0;
  width: 200px;
  background-color: ${(props) => props.theme.colors.secondary};
  max-height: 80px;
  padding: 16px 0;
  height: 0px;
  z-index: 1;
  padding: ${(props) => (props.isVisible ? '8px' : '0 8px')};
  transition: all 0.2s ease-in-out;
  overflow-y: auto;
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
`;

const ProfileDiscordConnectionMenuItem = styled.div<{
  isDropDownOpened: boolean;
}>`
  height: 20px;
  width: 100%;
  border-radius: 4px;
  cursor: pointer;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: #000;
  font-size: 14px;
  visibility: ${(props) => (props.isDropDownOpened ? 'visible' : 'hidden')};
  &:hover {
    background-color: ${(props) => `${props.theme.colors.accent}80`};
    color: #fff;
  }
`;

const ProfileDiscordElement = ({ conn, onDelete, onPrimary }) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const dropdownMenuRef = useRef<any>();
  const ref = useRef();

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const handleDropDownToggle = async () => {
    try {
      if (!isMenuVisible) {
        dropdownMenuRef.current.style.height = '80px';
        await sleep(100);
        setIsMenuVisible(true);
      } else {
        dropdownMenuRef.current.style.height = '0px';
        await sleep(10);
        setIsMenuVisible(false);
      }
    } catch (e) {}
  };

  useOnClickOutside(ref, () => {
    if (isMenuVisible) handleDropDownToggle();
  });

  return (
    <ProfileDiscordConnection
      ref={ref}
      onClick={() => {
        if (isMenuVisible) handleDropDownToggle();
      }}
      isDefault={conn.isPrimaryConnection}
    >
      <DiscordIcon />
      <p className="discord-id w-full">
        {conn.username} | {conn.discordUserId}{' '}
      </p>
      {conn.isPrimaryConnection && (
        <div className="default-discord">Primary</div>
      )}
      {!conn.isPrimaryConnection && (
        <button
          className="discord-kebab-menu"
          onClick={() => handleDropDownToggle()}
        >
          <KebabMenu />
        </button>
      )}

      <ProfileDiscordConnectionMenu
        isVisible={isMenuVisible}
        ref={dropdownMenuRef}
      >
        <ProfileDiscordConnectionMenuItem
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleDropDownToggle();
            onPrimary();
          }}
          isDropDownOpened={isMenuVisible}
        >
          Make Primary
        </ProfileDiscordConnectionMenuItem>
        <ProfileDiscordConnectionMenuItem
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleDropDownToggle();
            onDelete();
          }}
          isDropDownOpened={isMenuVisible}
        >
          Delete
        </ProfileDiscordConnectionMenuItem>
      </ProfileDiscordConnectionMenu>
    </ProfileDiscordConnection>
  );
};

const AccountDetails = ({ props, onRefetch }) => {
  const [email, setEmail] = useState(props.email);
  const [username, setUsername] = useState(props.username);
  const [firstName, setFirstName] = useState(props.firstName);
  const [lastName, setLastName] = useState(props.lastName);
  const [changed, setChanged] = useState(false);
  const [deleteDiscordConnection, deleteDiscordConnectionResult] = useMutation(
    DELETE_DISCORD_CONNECTION,
  );
  const [setPrimaryDiscordConnection, setPrimaryDiscordConnectionResult] =
    useMutation(SET_PRIMARY_DISCORD_CONNECTION);
  const [updateAccountInfo, updateAccountInfoResult] = useMutation(UPDATE_USER);

  useEffect(() => {
    if (
      props.firstName !== firstName ||
      props.lastName !== lastName ||
      props.username !== username
    ) {
      if (!changed) {
        setChanged(true);
      }
    }
  }, [username, firstName, lastName]);

  const onDelete = (conn) => {
    if (!props.discordConnections || props.discordConnections.length < 2)
      return null;
    // return () => {
    if (confirm('Are you sure you want to delete this Discord Connection?')) {
      deleteDiscordConnection({
        variables: { discordUserId: conn.discordUserId },
      })
        .then(() => {
          toast.success('Successfully deleted discord connection.');
          onRefetch();
        })
        .catch(() => {
          toast.error('Failed to delete discord connection.');
        });
    }
    // };
  };

  const onPrimary = (conn) => {
    if (!props.discordConnections || props.discordConnections.length < 2)
      return null;
    // return () => {
    if (
      confirm('Are you sure you want to make this Discord Connection primary?')
    ) {
      setPrimaryDiscordConnection({
        variables: { discordUserId: conn.discordUserId },
      })
        .then(() => {
          toast.success('Successfully marked primary discord connection.');
          onRefetch();
        })
        .catch(() => {
          toast.error('Failed to mark discord connection as primary.');
        });
    }
    // };
  };

  const updateAccount = () => {
    const updateUserData: {
      firstName: string;
      lastName: string;
      username?: string;
    } = {
      firstName,
      lastName,
    };

    if (props.username !== username) {
      updateUserData.username = username;
    }

    updateAccountInfo({
      variables: {
        id: props.id,
        updateUserData,
      },
    })
      .then(() => {
        analytics.track('User Updated Account', {
          old: props.username,
          new: updateUserData.username,
        });
        setChanged(false);
        onRefetch();
      })
      .catch(() => {});
  };

  return (
    <Container className="h-full">
      <Contents>
        <Title>
          <ProfileButtonWrapper>
            Account Details
            <TidalButton
              text="Save Account Details"
              onClick={updateAccount}
              type="info"
              disabled={changed !== true || updateAccountInfoResult.loading}
              tint={false}
            />
          </ProfileButtonWrapper>
        </Title>
        <TidalInputField
          label="Email Address"
          onChange={(e) => setEmail(e.target.value)}
          defaultValue={email}
          disabled={true}
          placeholder="Enter Email Address"
        />
        {/* <EditableInput disabled={true} onChangeText={setEmail} value={email} /> */}
        {/* <InputTitle>Username</InputTitle>
        <EditableInput onChangeText={setUsername} value={username} /> */}
        <Box height={16} />
        <TidalInputField
          label="Username"
          onChange={(e) => setUsername(e.target.value)}
          defaultValue={username}
          disabled={false}
          placeholder="Enter username"
        />
        <Box height={16} />
        <Row>
          <Column className="mr-5">
            {/* <EditableInput onChangeText={setFirstName} value={firstName} /> */}
            <TidalInputField
              label="First Name"
              onChange={(e) => setFirstName(e.target.value)}
              defaultValue={firstName}
              disabled={false}
              placeholder="Enter first name"
            />
          </Column>
          <Column>
            {/* <EditableInput onChangeText={setLastName} value={lastName} /> */}
            <TidalInputField
              label="Last Name"
              onChange={(e) => setLastName(e.target.value)}
              defaultValue={lastName}
              disabled={false}
              placeholder="Enter first name"
            />
          </Column>
        </Row>
        <Box height={28} />

        <ProfileButtonWrapper>
          <Title>Discord Accounts</Title>
          <TidalButton
            onClick={() => {
              window.location.replace('/register/connect-discord');
            }}
            type="info"
            tint={false}
            text=" Connect Discord Account"
          />
        </ProfileButtonWrapper>
        <Box height={16} />
        {props.discordConnections && props.discordConnections.length ? (
          props.discordConnections.map((conn, index) => {
            return (
              <ProfileDiscordElement
                onDelete={() => {
                  analytics.track('User Deleted Discord');
                  onDelete(conn);
                }}
                onPrimary={() => {
                  analytics.track('User Updated Primary Discord');
                  onPrimary(conn);
                }}
                conn={conn}
              />
            );
          })
        ) : (
          <Box height={20} />
        )}
      </Contents>
    </Container>
  );
};

export default AccountDetails;
